import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { isHttpClientError, resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import {
  APPROVAL_SETTINGS_CONFLICT_CODE,
  approvalMemberCandidatesQueryKey,
  approvalSettingsQueryKey,
  approvalSettlementSuppliersQueryKey,
} from "../config";
import {
  addStep,
  buildDepartmentTree,
  buildMemberTree,
  clearBranch,
  createSettingsDraft,
  enableBranch,
  fetchApprovalMemberCandidates,
  fetchApprovalSettings,
  moveStep,
  removeStep,
  saveApprovalSettings,
  searchSettlementSuppliers,
  setBranchDepartments,
  setBranchDimension,
  setBranchSuppliers,
  setStepApproverType,
  setStepMembers,
  validateSettingsDraft,
} from "../service/approvalSettingsService";
import type {
  ApprovalApproverType,
  ApprovalBranchDimension,
  ApprovalDraftErrors,
  ApprovalSettingsDraft,
  ApprovalTypeSettings,
} from "../types";

const noErrors: ApprovalDraftErrors = { flows: {}, messages: [] };

/** 名字与停用标记，编辑稿里只存 ID，显示时按 ID 查。 */
type NamedItem = { name: string; disabled: boolean };

/** 审批设置列表与编辑会话：只有能改设置的人才能打开编辑、取员工树和结算主体候选。 */
export function useApprovalSettingsRuntime(canUpdate: boolean) {
  const client = useQueryClient();
  const [draft, setDraft] = useState<ApprovalSettingsDraft | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supplierKeyword, setSupplierKeyword] = useState("");
  // 选中时记下名字：搜索结果随关键字换掉后，已选的结算主体仍按名字显示。
  const [pickedSuppliers, setPickedSuppliers] = useState<ReadonlyMap<string, NamedItem>>(new Map());
  const saveLock = useRef(false);
  const settingsQuery = useQuery({ queryKey: approvalSettingsQueryKey, queryFn: fetchApprovalSettings, refetchOnWindowFocus: false });
  const candidatesQuery = useQuery({
    queryKey: approvalMemberCandidatesQueryKey,
    queryFn: fetchApprovalMemberCandidates,
    enabled: canUpdate && draft !== null,
    refetchOnWindowFocus: false,
  });
  const usesSuppliers = Boolean(draft?.branches.some((branch) => branch?.scopeDimension === "settlement_supplier"));
  const keyword = supplierKeyword.trim();
  const suppliersQuery = useQuery({
    queryKey: [...approvalSettlementSuppliersQueryKey, keyword],
    queryFn: () => searchSettlementSuppliers(keyword),
    enabled: canUpdate && usesSuppliers,
    refetchOnWindowFocus: false,
  });
  const items = useMemo(() => settingsQuery.data ?? [], [settingsQuery.data]);
  const branches = useMemo(() => items.flatMap((item) => item.branches), [items]);
  const memberTree = useMemo(() => (candidatesQuery.data ? buildMemberTree(candidatesQuery.data) : []), [candidatesQuery.data]);
  const departmentTree = useMemo(
    () => (candidatesQuery.data ? buildDepartmentTree(candidatesQuery.data.departments) : []),
    [candidatesQuery.data],
  );
  /** 成员姓名和停用状态：先取设置里的，员工树到了再用最新的覆盖。 */
  const memberLookup = useMemo(() => {
    const lookup = new Map<number, NamedItem>();
    const steps = [...items.flatMap((item) => item.mainFlow ?? []), ...branches.flatMap((branch) => branch.steps)];
    for (const member of steps.flatMap((step) => step.members)) lookup.set(member.userId, { name: member.name, disabled: member.disabled });
    for (const member of candidatesQuery.data?.members ?? []) lookup.set(member.userId, { name: member.name, disabled: member.disabled });
    return lookup;
  }, [branches, candidatesQuery.data, items]);
  /** 结算主体名字：先取设置里的，再补选中时记下的和当前搜索结果。 */
  const supplierLookup = useMemo(() => {
    const lookup = new Map<string, NamedItem>();
    for (const item of branches.flatMap((branch) => branch.suppliers)) lookup.set(item.supplierId, { name: item.name, disabled: item.disabled });
    for (const [supplierId, item] of pickedSuppliers) lookup.set(supplierId, item);
    for (const item of suppliersQuery.data ?? []) lookup.set(item.supplierId, { name: item.name, disabled: item.disabled });
    return lookup;
  }, [branches, pickedSuppliers, suppliersQuery.data]);
  /** 部门名字：先取设置里的（含已不存在的），部门树到了再用最新的覆盖。 */
  const departmentLookup = useMemo(() => {
    const lookup = new Map<number, NamedItem>();
    for (const item of branches.flatMap((branch) => branch.departments)) lookup.set(item.deptId, { name: item.name, disabled: item.disabled });
    for (const dept of candidatesQuery.data?.departments ?? []) lookup.set(dept.deptId, { name: dept.name, disabled: false });
    return lookup;
  }, [branches, candidatesQuery.data]);
  const edit = (change: (current: ApprovalSettingsDraft) => ApprovalSettingsDraft) => {
    if (!saveLock.current) setDraft((current) => (current ? change(current) : current));
  };

  return {
    items,
    loading: settingsQuery.isFetching,
    queryError: settingsQuery.error ? "审批设置加载失败，请重试。" : "",
    refresh() { void settingsQuery.refetch(); },
    draft,
    errors: draft && submitted ? validateSettingsDraft(draft) : noErrors,
    errorMessage,
    conflict,
    saving,
    memberTree,
    memberLookup,
    departmentTree,
    departmentLookup,
    supplierLookup,
    /** 当前搜到的结算主体候选（只含能用的）。 */
    supplierResults: suppliersQuery.data ?? [],
    suppliersLoading: suppliersQuery.isFetching,
    suppliersError: suppliersQuery.error ? "结算主体加载失败，请重新输入关键字。" : "",
    searchSuppliers(value: string) { setSupplierKeyword(value); },
    candidatesLoading: candidatesQuery.isFetching,
    candidatesError: candidatesQuery.error ? "员工列表加载失败，请重试。" : "",
    retryCandidates() { void candidatesQuery.refetch(); },
    openEditor(settings: ApprovalTypeSettings) {
      if (!canUpdate || saveLock.current) return;
      setDraft(createSettingsDraft(settings));
      setSubmitted(false); setErrorMessage(""); setConflict(false); setSupplierKeyword("");
    },
    /** 关掉编辑；撞过版本冲突时顺带重新读取，免得下次打开又拿旧数据再撞一次。 */
    closeEditor() {
      if (saveLock.current) return;
      setDraft(null);
      if (conflict) { setConflict(false); void client.invalidateQueries({ queryKey: approvalSettingsQueryKey }); }
    },
    addStep(flow: number) { edit((current) => addStep(current, flow)); },
    removeStep(flow: number, index: number) { edit((current) => removeStep(current, flow, index)); },
    moveStep(flow: number, index: number, offset: -1 | 1) { edit((current) => moveStep(current, flow, index, offset)); },
    setStepMembers(flow: number, index: number, memberIds: number[]) { edit((current) => setStepMembers(current, flow, index, memberIds)); },
    setStepApproverType(flow: number, index: number, approverType: ApprovalApproverType) {
      edit((current) => setStepApproverType(current, flow, index, approverType));
    },
    enableBranch(branchNo: number) { edit((current) => enableBranch(current, branchNo)); },
    clearBranch(branchNo: number) { edit((current) => clearBranch(current, branchNo)); },
    setBranchDimension(branchNo: number, dimension: ApprovalBranchDimension) { edit((current) => setBranchDimension(current, branchNo, dimension)); },
    setBranchSuppliers(branchNo: number, supplierIds: string[]) {
      const picked = (suppliersQuery.data ?? []).filter((item) => supplierIds.includes(item.supplierId));
      if (picked.length) {
        setPickedSuppliers((current) => new Map([...current, ...picked.map((item) => [item.supplierId, { name: item.name, disabled: item.disabled }] as const)]));
      }
      edit((current) => setBranchSuppliers(current, branchNo, supplierIds));
    },
    setBranchDepartments(branchNo: number, departmentIds: number[]) { edit((current) => setBranchDepartments(current, branchNo, departmentIds)); },
    /** 先在前端校验，通过后整套保存；版本冲突时提示刷新，其他失败保留编辑内容。 */
    async save() {
      if (!draft || saveLock.current) return false;
      if (!canUpdate) { setErrorMessage("修改权限已变更，当前输入仍保留。"); return false; }
      setSubmitted(true); setErrorMessage(""); setConflict(false);
      if (validateSettingsDraft(draft).messages.length) return false;
      saveLock.current = true; setSaving(true);
      try {
        const saved = await saveApprovalSettings(draft);
        client.setQueryData<ApprovalTypeSettings[]>(approvalSettingsQueryKey, (old) =>
          old?.map((item) => (item.typeCode === saved.typeCode ? saved : item)));
        setDraft(null);
        return true;
      } catch (error) {
        const isConflict = isHttpClientError(error) && error.code === APPROVAL_SETTINGS_CONFLICT_CODE;
        setConflict(isConflict);
        setErrorMessage(resolveReadableHttpErrorMessage(error, "保存失败，请检查后重试。", {
          conflictCode: APPROVAL_SETTINGS_CONFLICT_CODE,
          conflictFallback: "审批设置已被其他人修改，请刷新后再改。",
        }));
        return false;
      } finally {
        saveLock.current = false; setSaving(false);
      }
    },
    /** 冲突后放弃本次编辑，重新读取最新设置。 */
    reloadAfterConflict() {
      setDraft(null); setConflict(false); setErrorMessage("");
      void client.invalidateQueries({ queryKey: approvalSettingsQueryKey });
    },
  };
}
